/**
 * The policy's files, and what an author may do with them.
 *
 * A policy carries a PDF — what staff read — and, when it was written in
 * Word, the .docx it came from, kept so the author can download it, edit
 * it and bring it back as the next version. Both are listed plainly: one
 * opens, one downloads.
 *
 * A NEW VERSION OF A PUBLISHED POLICY DOES NOT UNPUBLISH IT. The published
 * files stay where staff find them; the next version's files are a second
 * list underneath, visible to authors and approvers, with the author's
 * panels attached to it: replace the document, convert a Word file whose
 * PDF is missing, submit it, or discard it. That is how every policy system
 * a hospital has used behaves, and what a surveyor expects — a policy does
 * not vanish from the intranet because somebody opened Word.
 */

import { SubmitRoute, type RouteChoice } from '../approvals/SubmitRoute';
import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { policyCapabilities } from './policyCapabilities';
import { SUMMARY_MAX } from '../../types/fieldLimits';
import { isCallFailure } from '../../lib/callable';
import { CharCount } from '../../shared/CharCount';
import { FileDrop } from '../../shared/FileDrop';
import { ACCEPT, formatBytes, rejectReason } from './useUpload';
import {
  authoritative, isDocx, isPdf, readableAttachment, useRevisionFiles, wordOriginal,
  type Attachment, type FileSet, type PolicyDetail,
} from './usePolicy';
import { useFileUrl } from './useFileUrl';
import {
  useConvertAttachment, useDiscardRevision, useReplaceDocument, useStartRevision, useSubmitDraft, type ReplaceStage,
} from './useDocument';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './DocumentSection.module.css';

/** The backend's minimum for a change summary. */
export const CHANGE_SUMMARY_MIN = 10;

const STAGE_TEXT: Record<ReplaceStage, string> = {
  idle: '', checking: 'Checking the file…', uploading: 'Uploading…', attaching: 'Attaching…',
  converting: 'Converting the Word document to PDF…', clearing: 'Removing the previous file…',
};

export function attachmentRole(a: Attachment): string {
  if (isPdf(a)) return a.generatedFrom ? 'PDF, converted from the Word document — what staff read' : 'PDF — what staff read';
  if (isDocx(a)) return 'Word original — download to edit, then upload as the next version';
  return a.contentType;
}

/**
 * Which list of files an author is working on: a draft's own, the next
 * version's on a published policy, or none.
 */
export function editableFiles(policy: Pick<PolicyDetail, 'status' | 'revisionStatus'>): 'attachments' | 'revisionAttachments' | null {
  if (policy.status === 'draft') return 'attachments';
  if (policy.status === 'published' && policy.revisionStatus === 'draft') return 'revisionAttachments';
  return null;
}

type Panel = 'replace' | 'submit' | 'revise' | 'discard' | null;

/**
 * One file, with the one thing it is for. A Word original is downloaded
 * through a URL minted for this reader and good for fifteen minutes; nothing
 * on the record is a link anybody could use (blocker B2).
 */
function FileRow({ policyId, file, isDocument, viewQuery }: {
  policyId: string; file: Attachment; isDocument: boolean; viewQuery: string;
}) {
  const word = isDocx(file);
  const minted = useFileUrl(policyId, word ? file : null);
  return (
    <li className={styles.listItem}>
      <div>
        <div className={styles.itemName}>{file.name}</div>
        <div className={styles.itemMeta}>
          {isDocument ? attachmentRole(file) : 'Replaced — not part of this document'}
          {file.size != null && ` · ${formatBytes(file.size)}`}
        </div>
      </div>
      {isPdf(file) && isDocument ? (
        <Link to={`/policy/${policyId}/view${viewQuery}`} className={`${styles.btn} ${styles.btnPrimary}`}>
          {viewQuery ? 'Open draft' : 'Open document'}
        </Link>
      ) : word && isDocument ? (
        <a
          href={minted.data?.url ?? '#'}
          download={file.name}
          className={styles.btn}
          aria-disabled={!minted.data}
        >
          Download Word file
        </a>
      ) : null}
    </li>
  );
}

/**
 * The files of one set, the document first.
 *
 * A file the author replaced can still be in the set — its removal runs after
 * the switch and is allowed to fail (blocker B6) — so the list says which
 * file is the document and which are leftovers, rather than letting position
 * imply it.
 */
function FileList({ policyId, set, viewQuery, empty }: { policyId: string; set: FileSet; viewQuery: string; empty: string }) {
  if (set.attachments.length === 0) return <p className={own.empty}>{empty}</p>;
  const doc = authoritative(set);
  const partner = doc ? set.attachments.find((a) => a.generatedFrom === doc.id || a.id === doc.generatedFrom) : undefined;
  const belongs = new Set([doc?.id, partner?.id].filter(Boolean) as string[]);
  const ordered = [...set.attachments].sort((a, b) => Number(belongs.has(b.id)) - Number(belongs.has(a.id)));
  return (
    <ul className={styles.list}>
      {ordered.map((a) => (
        <FileRow key={a.id} policyId={policyId} file={a} isDocument={belongs.has(a.id)} viewQuery={viewQuery} />
      ))}
    </ul>
  );
}

export function DocumentSection({ policy }: { policy: PolicyDetail }) {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const roles = session?.roles ?? [];
  /**
   * The same rule the callables use. This panel used to gate purely on the
   * `author` role with no ownership test at all, so every author in the
   * organization was invited to replace the document on a colleague's draft
   * and was refused AFTER the browser had uploaded the file.
   */
  const can = policyCapabilities(policy, { uid: session?.uid ?? null, roles });
  const published = policy.status === 'published';
  const editing = editableFiles(policy);
  const revising = editing === 'revisionAttachments';
  const revisionInReview = published && policy.revisionStatus === 'in_review';
  const hasRevision = revising || revisionInReview;
  const nextVersion = (policy.currentVersionNumber ?? policy.versionNumber ?? 0) + 1;

  const replace = useReplaceDocument(orgId, policy.id);
  const convert = useConvertAttachment(orgId, policy.id);
  const revise = useStartRevision(orgId, policy.id);
  const discard = useDiscardRevision(orgId, policy.id);
  const submit = useSubmitDraft(orgId, policy.id);

  const [panel, setPanel] = useState<Panel>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [stage, setStage] = useState<ReplaceStage>('idle');
  const [summary, setSummary] = useState('');
  const [routeChoice, setRouteChoice] = useState<RouteChoice>({ ready: true });
  const [done, setDone] = useState<string | null>(null);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  /**
   * The files the author is working on: the next version's when one is open,
   * the draft's otherwise. The next version's live in a document of their own
   * that only authors, reviewers, approvers and administrators may read, so a
   * viewer who reaches this page sees an empty set and no draft (blocker B2).
   */
  const revision = useRevisionFiles(orgId, policy.id, hasRevision);
  const revisionFiles: FileSet = revision.data ?? { attachments: [], documentId: null };
  const own_: FileSet = { attachments: policy.attachments, documentId: policy.documentId };
  const working = hasRevision ? revisionFiles : own_;
  const pdf = readableAttachment(working);
  const word = wordOriginal(working);
  /**
   * The next version's files are readable by the roles that write and approve
   * documents; the rules refuse everybody else, so a viewer sees an empty set
   * either way and this only decides whether to draw the panel at all.
   */
  const canSeeRevision = revision.data !== undefined && !revision.isError;
  /**
   * The server takes a policy whose body is text with no file at all, so the
   * button is only held back when there is neither.
   */
  const readyToSubmit = Boolean(pdf) || Boolean(policy.content?.trim());
  const busy = replace.isPending || convert.isPending || revise.isPending || submit.isPending || discard.isPending;
  const error = replace.error ?? convert.error ?? revise.error ?? submit.error ?? discard.error;

  function open(p: Panel) {
    setPanel(panel === p ? null : p);
    setDone(null); setFile(null); setFileError(null); setSummary('');
  }

  function chooseFile(f: File) {
    const why = rejectReason(f);
    setFileError(why);
    setFile(why ? null : f);
  }

  function doReplace(e: FormEvent) {
    e.preventDefault();
    if (!file) { setFileError(rejectReason(null)); return; }
    replace.mutate({ file, previous: working.attachments, onStage: setStage }, {
      onSuccess: (r) => {
        setStage('idle'); setPanel(null); setFile(null);
        setDone(r.conversionFailed
          ? `The Word document is on the draft, but converting it to PDF failed: ${r.conversionFailed}`
          : 'The document has been replaced.');
      },
      onError: () => setStage('idle'),
    });
  }

  function doSubmit(e: FormEvent) {
    e.preventDefault();
    if (summary.trim().length < CHANGE_SUMMARY_MIN || !routeChoice.ready) return;
    submit.mutate(routeChoice.workflowId ? { changeSummary: summary, workflowId: routeChoice.workflowId, workflowReason: routeChoice.workflowReason } : summary, {
      onSuccess: () => {
        setPanel(null);
        setDone(revising
          ? `Version ${nextVersion} is submitted for approval. Staff keep reading version ${policy.currentVersionNumber ?? ''} until it is approved.`
          : 'Submitted for approval. The approvers have been emailed.');
      },
    });
  }

  function doRevise() {
    revise.mutate(undefined, {
      onSuccess: (r) => {
        setPanel(null);
        setDone(`A draft for version ${r.nextVersionNumber} is open below. Replace the document, then submit it for approval. Staff keep reading the published version meanwhile.`);
      },
    });
  }

  function doDiscard() {
    discard.mutate(undefined, {
      onSuccess: () => { setPanel(null); setDone('The new version was discarded. The published version is unchanged.'); },
    });
  }

  const actions = can.replaceDocument || can.submit || can.startRevision || can.discardRevision;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Document</h2>

      {done && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{done}</div>}
      {error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(error) ? error.message : error instanceof Error ? error.message : 'That could not be done.'}
        </div>
      )}

      <FileList policyId={policy.id} set={own_} viewQuery="" empty="No file is attached yet." />

      {published && hasRevision && canSeeRevision && (
        <div className={own.panel} style={{ maxWidth: 'none' }} role="group" aria-labelledby={id('next-t')}>
          <h3 className={own.panelTitle} id={id('next-t')}>
            Next version (v{nextVersion}) — {revising ? 'draft' : 'in review'}
          </h3>
          <p className={form.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            {revising
              ? 'Only authors and approvers see these files. Staff keep reading the published version above until this one is approved.'
              : 'Waiting on approval. Staff keep reading the published version above until it is approved.'}
          </p>
          <FileList policyId={policy.id} set={revisionFiles} viewQuery="?file=next" empty="No file is attached to the new version yet." />
        </div>
      )}

      {word && !pdf && can.convertToPdf && (
        <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
          The Word document has no PDF yet, so staff cannot read it.{' '}
          <button type="button" className={own.linkBtn} disabled={busy} onClick={() => convert.mutate(word.id, { onSuccess: () => setDone('Converted. Staff can read the PDF now.') })}>
            {convert.isPending ? 'Converting…' : 'Convert it to PDF now'}
          </button>
        </div>
      )}

      {actions && (
        <div className={styles.actions} style={{ marginTop: 'var(--sp-4)' }}>
          {can.replaceDocument && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'replace'} onClick={() => open('replace')}>Replace the document…</button>}
          {can.submit && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !readyToSubmit} aria-expanded={panel === 'submit'} onClick={() => open('submit')}>Submit for approval…</button>}
          {can.discardRevision && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'discard'} onClick={() => open('discard')}>Discard the new version…</button>}
          {can.startRevision && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'revise'} onClick={() => open('revise')}>Start a new version…</button>}
        </div>
      )}

      {panel === 'replace' && (
        <form className={own.panel} onSubmit={doReplace} aria-labelledby={id('rep-t')}>
          <h3 className={own.panelTitle} id={id('rep-t')}>Replace the document</h3>
          <p className={form.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            The file you choose takes the place of what is attached to {revising ? 'the new version' : 'the draft'} now. A Word document is kept as the original and converted to PDF.
          </p>
          <label className={form.label} htmlFor={id('file')}>New file</label>
          <FileDrop id={id('file')} accept={ACCEPT} prompt="Choose a PDF or Word document" onFile={chooseFile} invalid={Boolean(fileError)} describedBy={id('file-hint')} disabled={busy} />
          <p className={fileError ? form.error : form.hint} id={id('file-hint')}>
            {fileError ?? (file ? `Selected: ${file.name} (${formatBytes(file.size)})` : 'PDF or Word (.docx), up to 25 MB.')}
          </p>
          {replace.isPending && <p className={form.hint} role="status" aria-live="polite">{STAGE_TEXT[stage]}</p>}
          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !file}>{replace.isPending ? 'Working…' : 'Replace'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </form>
      )}

      {panel === 'submit' && (
        <form className={own.panel} onSubmit={doSubmit} aria-labelledby={id('sub-t')}>
          <h3 className={own.panelTitle} id={id('sub-t')}>Submit for approval</h3>
          <div className={form.field}>
            <label className={form.label} htmlFor={id('sum')}>What changed</label>
            <textarea id={id('sum')} className={form.textarea} rows={3} required maxLength={SUMMARY_MAX} value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, SUMMARY_MAX))} aria-describedby={id('sum-hint')} />
            <CharCount value={summary} max={SUMMARY_MAX} />
            <p className={form.hint} id={id('sum-hint')}>Shown to the approvers and in What's New when it is published. At least {CHANGE_SUMMARY_MIN} characters.</p>
          </div>
          <SubmitRoute policyId={policy.id} idPrefix={id('route')} onChange={setRouteChoice} />
          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || summary.trim().length < CHANGE_SUMMARY_MIN || !routeChoice.ready}>{submit.isPending ? 'Submitting…' : 'Submit'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </form>
      )}

      {panel === 'revise' && (
        <div className={own.panel} role="group" aria-labelledby={id('rev-t')}>
          <h3 className={own.panelTitle} id={id('rev-t')}>Start a new version?</h3>
          <p className={form.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            A draft for version {nextVersion} opens with a copy of the current files, so you can download the Word original, edit it, and upload it as the next version.
            The published version {policy.currentVersionNumber ?? policy.versionNumber ?? ''} stays available to staff the whole time, and stays on record afterwards.
          </p>
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={doRevise}>{revise.isPending ? 'Starting…' : 'Start the new version'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </div>
      )}

      {panel === 'discard' && (
        <div className={own.panel} role="group" aria-labelledby={id('dis-t')}>
          <h3 className={own.panelTitle} id={id('dis-t')}>Discard the new version?</h3>
          <p className={form.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            The draft for version {nextVersion} and its files are dropped. The published version is not touched. This cannot be undone.
          </p>
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={doDiscard}>{discard.isPending ? 'Discarding…' : 'Discard it'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
